"use client";

/**
 * PolicyEditDialog - Redesigned to match AddPolicyDialog
 * Modern card-based UI with icons and hover effects
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, X, FileText, Download } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faXmark, 
  faFileLines, 
  faUpload,
  faSparkles,
  faShieldCheck,
  faUsers,
  faLink,
  faUserTie,
  faCloudArrowUp,
  faDownload,
  faCalendarClock,
  faRepeat
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DatePicker } from "@/components/ui/date-picker";
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";
import { auth } from '@/lib/firebase';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { MultiUserCombobox } from '@/components/ui/MultiUserCombobox';

import {
  assignPolicy,
  assignPolicyToAll,
  unassignPolicy,
  unassignPolicyToAll,
  reviewPolicy,
  deletePolicy
} from '@/services/policyService';

// Type Imports
import { Policy } from "@/types/policy";
import { getDocument as getPdfDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import { ControlCombobox } from '@/components/ui/ControlCombobox';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';

// Configure PDF.js worker
GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { getAuthUID } from '@/lib/authState';
import { createDocument } from '@/services/documentsService';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';

interface PolicyEditDialogProps {
  companyID: string;
  UID: string;
  policy: Policy;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedPolicy: Policy, fileToUpload: File | null) => Promise<void>;
  isReadOnly?: boolean;
  userIdToName?: Record<string, string>;
}

// Format file size with appropriate units
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  // Use 0 decimals for bytes, 1 for KB, 2 for larger
  const decimals = i === 0 ? 0 : i === 1 ? 1 : 2;
  return `${size.toFixed(decimals)} ${sizes[i]}`;
};

const PolicyEditDialog: React.FC<PolicyEditDialogProps> = ({ policy, isOpen, onClose, onSave, companyID, UID, isReadOnly = false, userIdToName = {} }: PolicyEditDialogProps) => {
  const allowDOCX = true;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formState, setFormState] = useState<Policy>({
    ...policy,
    policy_name: policy?.policy_name ?? '',
    policyCategory: policy?.policyCategory ?? '',
    policy_version: policy?.policy_version ?? '',
    policy_status: policy?.policy_status ?? '',
    policy_metadata: {
      ...policy?.policy_metadata,
      policyType: policy?.policy_metadata?.policyType ?? 'default',
      confluence: policy?.policy_metadata?.confluence ?? false
    },
    // policy_owner: (policy?.policy_owner || policy?.policy_history?.policy_owner) ?? '',
    assignedUsers: policy?.assignedUsers ?? [], // Use this to then /deiterateInternalAPI/policies/assignPolicy (functions/deiterate_internal_api/src/routes/policies/assignPolicy.route.ts) route
    relatedControls: policy?.relatedControls,
    componentID: policy?.componentID ?? '',
  });

  const [formErrors, setFormErrors] = useState({
    policy_name: '',
    policyCategory: '',
    policy_version: '',
  });

  /* Joins the Related Controls with ', ' for displaying */
  const joinWithSpace = (arr: (string | number)[]) => {
    if (!Array.isArray(arr)) return 'None';

    return arr.join(', ');
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [isImportingConfluence, setIsImportingConfluence] = useState(false);
  const [confluenceError, setConfluenceError] = useState<string | null>(null);
  const [fileChanged, setFileChanged] = useState(false);
  const [showReviewWarning, setShowReviewWarning] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Review Schedule state
  type RepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  const frequencyOptions: { value: RepeatFrequency; label: string }[] = [
    { value: 'never', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
  ];
  
  // Initialize dates from policy data
  const [lastReviewDate, setLastReviewDate] = useState<Date | null>(() => {
    const lastReviewed = policy?.policy_history?.last_reviewed;
    return lastReviewed ? cleanDateAndTimeObject(lastReviewed) : null;
  });
  
  const [nextReviewDate, setNextReviewDate] = useState<Date | null>(() => {
    // Try to get from various possible locations
    const nextReview = (policy as any)?.nextReviewDate || (policy as any)?.next_review_date;
    if (nextReview) return cleanDateAndTimeObject(nextReview);
    // Default to 1 year from now if no date set
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  });
  
  const [repeatFrequency, setRepeatFrequency] = useState<RepeatFrequency>(() => {
    const repeat = (policy as any)?.repeat?.frequency;
    return repeat || 'annually';
  });
  const [repeatInterval, setRepeatInterval] = useState(() => {
    return (policy as any)?.repeat?.interval || 1;
  });
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const start = (policy as any)?.repeat?.startDate;
    return start ? cleanDateAndTimeObject(start) : null;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const end = (policy as any)?.repeat?.endDate;
    return end ? cleanDateAndTimeObject(end) : null;
  });
  
  const isRecurring = repeatFrequency !== 'never';
  
  // Auto-set start date when switching to recurring
  useEffect(() => {
    if (isRecurring && !startDate) {
      const base = cleanDateAndTimeObject(nextReviewDate ?? new Date()) ?? new Date();
      setStartDate(base);
    }
    if (!isRecurring) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [isRecurring, nextReviewDate, startDate]);
  
  // Schedule summary for display
  const scheduleSummary = useMemo(() => {
    if (!isRecurring || !startDate) return null;
    const intervalLabel = repeatInterval === 1 ? 'Repeats' : `Repeats every ${repeatInterval}`;
    const unit = repeatFrequency === 'daily'
      ? repeatInterval === 1 ? 'day' : 'days'
      : repeatFrequency === 'weekly'
        ? repeatInterval === 1 ? 'week' : 'weeks'
        : repeatFrequency === 'monthly'
          ? repeatInterval === 1 ? 'month' : 'months'
          : repeatFrequency === 'quarterly'
            ? repeatInterval === 1 ? 'quarter' : 'quarters'
            : repeatInterval === 1 ? 'year' : 'years';
    const endText = endDate ? ` until ${toLocalDateString(endDate)}` : '';
    return `${intervalLabel} ${repeatInterval === 1 ? '' : unit} starting ${toLocalDateString(startDate)}${endText}`;
  }, [isRecurring, startDate, endDate, repeatInterval, repeatFrequency]);

  // Download handler for document library policies
  const handleDownload = async () => {
    // Try multiple possible ID fields
    const docId = formState.id || 
      (policy as any)?.docID || 
      (policy as any)?.id ||
      (formState as any)?.docID;
    
    // Try multiple possible filename fields
    const fileName = (formState as any).file_name || 
      formState.policy_metadata?.file_name || 
      (policy as any)?.fileName ||
      (policy as any)?.file_name ||
      (formState as any)?.fileName ||
      (policy as any)?.displayName ||
      (formState as any)?.displayName ||
      formState.policy_name; // Fallback to policy name as last resort
    
    console.log('[PolicyEditDialog] Download attempt - docId:', docId, 'fileName:', fileName, 'policy:', policy, 'formState:', formState);
    
    if (!docId || !fileName) {
      toast.error('Unable to download: missing document information');
      console.error('[PolicyEditDialog] Missing download info - docId:', docId, 'fileName:', fileName);
      return;
    }

    setIsDownloading(true);
    try {
      // Document library path pattern
      const storagePath = `/customers/${companyID}/keyDocuments/otherDocs/uploaded/${docId}/${fileName}`;
      const fileRef = ref(storage, storagePath);
      const downloadUrl = await getDownloadURL(fileRef);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download started');
    } catch (error: any) {
      console.error('[PolicyEditDialog] Download error:', error);
      if (error?.code === 'storage/object-not-found') {
        toast.error('File not found in storage');
      } else {
        toast.error('Failed to download file');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Check if this policy has a downloadable file
  const hasDownloadableFile = useMemo(() => {
    const policyType = formState.policy_metadata?.policyType;
    // Check multiple possible filename fields
    const hasFileName = !!(formState as any).file_name || 
      !!formState.policy_metadata?.file_name || 
      !!(policy as any)?.fileName ||
      !!(policy as any)?.file_name ||
      !!(formState as any).fileName ||
      !!(policy as any)?.displayName;
    // Document library policies always have files, upload policies need a filename
    return policyType === 'document' || (policyType === 'upload' && hasFileName);
  }, [formState.policy_metadata?.policyType, formState, policy]);

  const handleChange = (field: keyof typeof formState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const formatPolicyName = (fileName: string): string => {
    // Remove file extensions (.pdf, .doc, .docx)
    let name = fileName.replace(/\.(pdf|docx?|doc)$/i, '');

    // Replace common separators with spaces: underscores, hyphens, dots
    name = name.replace(/[_\-\.]+/g, ' ');

    // Replace camelCase and PascalCase with spaces
    // e.g., "SecurityPolicy" -> "Security Policy"
    name = name.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Replace multiple spaces with single space
    name = name.replace(/\s+/g, ' ');

    // Trim whitespace
    name = name.trim();

    // Capitalize first letter of each word
    name = name.replace(/\b\w/g, (char) => char.toUpperCase());

    return name;
  };

  const calculateDocumentMetadata = async (file: File) => {
    try {
      console.log('[calculateDocumentMetadata] File type:', file.type, 'Size:', file.size);

      // For PDF files, extract actual page count
      if (file.type === 'application/pdf') {
        console.log('[calculateDocumentMetadata] Processing PDF...');
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await getPdfDocument({ data: arrayBuffer }).promise;
        const numPages = pdf.numPages;

        console.log('[calculateDocumentMetadata] PDF pages:', numPages);

        // Calculate read time: average reading speed is 200-250 words per minute
        // Assume ~250 words per page for PDFs
        const wordsPerPage = 250;
        const readingSpeedWPM = 200;
        const totalWords = numPages * wordsPerPage;
        const readTimeMinutes = Math.ceil(totalWords / readingSpeedWPM);

        return {
          pageCount: numPages,
          readTime: readTimeMinutes.toString()
        };
      }

      // For DOCX/DOC files, estimate based on file size
      // Average: 1 page ≈ 50KB for DOCX (they're compressed)
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword') {
        console.log('[calculateDocumentMetadata] Processing DOCX/DOC...');
        const fileSizeKB = file.size / 1024;
        // DOCX files are compressed, so use a more realistic estimate
        const estimatedPages = Math.max(1, Math.ceil(fileSizeKB / 50));

        console.log('[calculateDocumentMetadata] DOCX estimated pages:', estimatedPages);

        const wordsPerPage = 250;
        const readingSpeedWPM = 200;
        const totalWords = estimatedPages * wordsPerPage;
        const readTimeMinutes = Math.ceil(totalWords / readingSpeedWPM);

        return {
          pageCount: estimatedPages,
          readTime: readTimeMinutes.toString()
        };
      }

      console.warn('[calculateDocumentMetadata] Unknown file type:', file.type);
      return null;
    } catch (error) {
      console.error('[calculateDocumentMetadata] Error:', error);
      return null;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedTypes = [
        'application/pdf',
      ];

      if (allowDOCX) {
        allowedTypes.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword');
      }

      if (!allowedTypes.includes(file.type)) {
        setErrorMessage('Only PDF or DOC/DOCX files are allowed');
        return;
      }
      setFileInput(file);
      setFileChanged(true); // Mark that file has been changed
      setErrorMessage(null);

      // Calculate document metadata (works for PDF and DOCX)
      const metadata = await calculateDocumentMetadata(file);

      // Extract and format policy name from filename
      const policyName = formatPolicyName(file.name);

      // Update policy metadata with file info and calculated values
      setFormState(prev => ({
        ...prev,
        policy_name: policyName,
        pagesCount: metadata?.pageCount.toString() || prev.pagesCount,
        ReadTime: metadata?.readTime || prev.ReadTime,
        policy_metadata: {
          ...prev.policy_metadata,
          file_name: file.name
        }
      }));
    }
  };

  const handlePolicySourceChange = (value: string) => {
    setFormState(prev => ({
      ...prev,
      policy_metadata: {
        ...prev.policy_metadata,
        policyType: value as 'default' | 'upload'
      }
    }));
  };

  const handleCustomSourceChange = (value: string) => {
    setFormState(prev => ({
      ...prev,
      policy_metadata: {
        ...prev.policy_metadata,
        confluence: value === 'confluence',
        // Reset confluenceURL when switching sources
        ...(value !== 'confluence' && { confluenceURL: '' })
      }
    }));
    setConfluenceError(null);
  };

  const handlePullConfluence = async () => {
    const confluenceURL = formState.policy_metadata?.confluenceURL;
    if (!confluenceURL) {
      setConfluenceError('Please enter a Confluence URL');
      return;
    }

    try {
      setIsImportingConfluence(true);
      setConfluenceError(null);

      // Extract page ID from Confluence URL
      const urlObj = new URL(confluenceURL);
      const pathSegments = urlObj.pathname.split('/');
      const pagesIndex = pathSegments.indexOf('pages');
      const pageId = pagesIndex !== -1 && pagesIndex + 1 < pathSegments.length
        ? pathSegments[pagesIndex + 1]
        : null;

      if (!pageId) {
        throw new Error('Could not extract page ID from Confluence URL. Make sure the URL contains "/pages/PAGE_ID/"');
      }

      // Get auth token
      const idToken = await getValidToken(auth);
      if (!idToken) {
        throw new Error('Not authenticated');
      }

      // Call Confluence Export API
      const response = await fetch(
        `${import.meta.env.VITE_CONFLUENCE_API_URL}/export/${pageId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'customer-id': companyID
          }
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to export Confluence page: ${error}`);
      }

      // Convert response to Blob and create File
      const arrayBuffer = await response.arrayBuffer();
      const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });

      // Extract filename from Content-Disposition header or use a default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'confluence-export.pdf';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Set the file for upload
      setFileInput(file);
      setFileChanged(true); // Mark that file has been changed

      // Calculate document metadata (works for PDF and DOCX)
      const metadata = await calculateDocumentMetadata(file);

      // Extract and format policy name from filename
      const policyName = formatPolicyName(fileName);

      // Update form state with file name and calculated values
      // Mark as confluence import AND set policyType to 'upload' so it gets saved to document library
      setFormState(prev => ({
        ...prev,
        policy_name: policyName,
        file_name: fileName,
        fileName: fileName,
        pagesCount: metadata?.pageCount.toString() || prev.pagesCount,
        ReadTime: metadata?.readTime || prev.ReadTime,
        policy_metadata: {
          ...prev.policy_metadata,
          policyType: 'upload', // This triggers the upload-to-document-library flow
          confluence: true, // Mark as Confluence import
          file_type: 'application/pdf',
          file_size: file.size,
          file_name: fileName
        }
      }));

      toast.success('Confluence document imported successfully');

    } catch (error: any) {
      console.error('Error importing Confluence document:', error);
      setConfluenceError(error.message || 'Failed to import Confluence document');
      toast.error(`Error importing Confluence document: ${error.message}`);
    } finally {
      setIsImportingConfluence(false);
    }
  };

  const handleSubmit = async () => {
    const newErrors = {
      policy_name: formState.policy_name ? '' : 'Required',
      policyCategory: formState.policyCategory ? '' : 'Required',
      policy_version: formState.policy_version ? '' : 'Required',
    };

    setFormErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) return;

    // If file changed, show warning dialog first
    if (fileChanged && !showReviewWarning) {
      setShowReviewWarning(true);
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Check if this is an upload type policy with a file - convert to document library policy
      // Also include Confluence imports (when policyType is 'upload' with confluence flag, or has fileInput from Confluence)
      const isConfluenceImport = formState.policy_metadata?.confluence && fileInput;
      const isUploadingNewFile = (formState.policy_metadata?.policyType === 'upload' && fileInput) || isConfluenceImport;
      const isDocumentPolicy = formState.policy_metadata?.policyType === 'document';
      const wasDefaultPolicy = policy?.policy_metadata?.policyType === 'default' || !policy?.policy_metadata?.policyType;
      
      // Build repeat config if recurring
      const repeatConfig = isRecurring && startDate ? {
        frequency: repeatFrequency,
        interval: repeatInterval,
        startDate: startDate,
        endDate: endDate,
      } : undefined;
      
      if (isDocumentPolicy) {
        // Save existing document-type policy using documentsService
        const { getDocument, saveDocument } = await import('@/services/documentsService');
        let docInstance = await getDocument(formState.id, companyID);
        if (docInstance) {
          // Update all editable fields
          docInstance.displayName = formState.policy_name || docInstance.displayName;
          docInstance.documentType = formState.policyCategory || 'Policy';
          docInstance.status = formState.policy_status || 'Draft';
          docInstance.version = formState.policy_version || docInstance.version || '1.0';
          docInstance.relatedControls = formState.relatedControls || [];
          docInstance.assignedUsers = formState.assignedUsers || [];
          docInstance.procRef = (formState.procRef || formState.relatedControls || []).map(String);
          docInstance.uploadedBy = formState.policy_history?.policy_owner || docInstance.uploadedBy || getAuthUID() || window.activeTenancy.tenancyOwner || '';
          
          // Review dates - use the recurring start date for the compliance calendar, not today
          if (isRecurring && startDate) {
            // For recurring reviews, set the first review date as the start date
            docInstance.nextReviewDate = startDate;
            docInstance.repeat = repeatConfig;
            docInstance.addToCalendar = true;
          } else {
            // For one-time reviews
            docInstance.nextReviewDate = nextReviewDate;
            docInstance.repeat = undefined;
            docInstance.addToCalendar = !!nextReviewDate;
          }
          
          // Update last review date if provided
          if (formState.policy_history?.last_reviewed) {
            docInstance.lastReviewDate = formState.policy_history.last_reviewed;
          }
          
          console.log('[PolicyEditDialog] Saving document policy:', docInstance);
          await saveDocument(docInstance, fileInput, companyID);
        } else {
          // If not found, create a new document
          const newDocData: any = {
            fileName: formState.policy_name,
            displayName: formState.policy_name,
            documentType: 'Policy',
            documentCategory: 'policy',
            status: formState.policy_status || 'Published',
            version: formState.policy_version || '1.0',
            uploadedBy: formState.policy_history?.policy_owner || getAuthUID() || '',
            dateUploaded: new Date(),
            relatedControls: formState.relatedControls || [],
            assignedUsers: formState.assignedUsers || [],
            // Use startDate for recurring, nextReviewDate for one-time
            nextReviewDate: isRecurring ? startDate : nextReviewDate,
            repeat: repeatConfig,
            addToCalendar: isRecurring || !!nextReviewDate,
            lastReviewDate: formState.policy_history?.last_reviewed || undefined,
          };
          console.log('[PolicyEditDialog] Creating new document policy:', newDocData);
          await createDocument(newDocData, fileInput, companyID);
        }
      } else if (isUploadingNewFile) {
        // User is uploading a file or importing from Confluence - create a new document library policy
        console.log('[PolicyEditDialog] Converting to document library policy', isConfluenceImport ? '(from Confluence)' : '(file upload)');
        console.log('[PolicyEditDialog] fileInput:', fileInput);
        
        // Get the actual filename from the File object for storage
        const actualFileName = fileInput?.name || (formState as any).fileName || (formState as any).file_name || formState.policy_name;
        
        // Create new document in document library
        const newDocData: any = {
          fileName: actualFileName, // Use actual file name for storage path
          displayName: formState.policy_name, // Use policy name for display
          documentType: 'Policy',
          documentCategory: 'policy',
          status: formState.policy_status || 'Published',
          version: formState.policy_version || '1.0',
          description: '',
          uploadedBy: formState.policy_history?.policy_owner || getAuthUID() || '',
          dateUploaded: new Date(),
          relatedControls: formState.relatedControls || [],
          assignedUsers: formState.assignedUsers || [],
          pagesCount: formState.pagesCount,
          ReadTime: formState.ReadTime,
          // Use startDate for recurring, nextReviewDate for one-time
          nextReviewDate: isRecurring ? startDate : nextReviewDate,
          repeat: repeatConfig,
          addToCalendar: isRecurring || !!nextReviewDate,
          lastReviewDate: formState.policy_history?.last_reviewed || undefined,
          // Include Confluence URL if this was a Confluence import
          confluenceURL: isConfluenceImport ? formState.policy_metadata?.confluenceURL : undefined,
        };
        
        console.log('[PolicyEditDialog] Creating document with data:', newDocData);
        const newDoc = await createDocument(newDocData, fileInput, companyID);
        console.log('[PolicyEditDialog] Created new document library policy:', newDoc);
        
        // If this was a default de.iterate policy, delete the old one
        if (wasDefaultPolicy && formState.policyRef) {
          try {
            await deletePolicy(companyID, formState.policyRef);
            console.log('[PolicyEditDialog] Deleted old default policy:', formState.policyRef);
          } catch (delError) {
            console.warn('[PolicyEditDialog] Could not delete old policy (may not exist):', delError);
            // Don't fail the operation if we can't delete the old policy
          }
        }
        
        onClose();
        toast.success(isConfluenceImport ? 'Confluence document saved to document library' : 'Policy uploaded to document library successfully');
        return;
      } else {
        // Default policy or no file upload - use standard save
        // ensure owner saves properly
        formState.policy_owner = formState.policy_owner || formState.policy_history?.policy_owner || getAuthUID() || window.activeTenancy.tenancyOwner || '';
        formState.policy_history.policy_owner = formState.policy_owner || formState.policy_history?.policy_owner || getAuthUID() || window.activeTenancy.tenancyOwner || '';
        formState.owner = formState?.policy_owner || formState.policy_history?.policy_owner || getAuthUID() || window.activeTenancy.tenancyOwner || '';
        formState.ReadTime = formState.ReadTime || '0';
        formState.pagesCount = formState.pagesCount || '0';
        
        // Add review dates to formState for default policies
        (formState as any).nextReviewDate = isRecurring ? undefined : nextReviewDate;
        (formState as any).repeat = repeatConfig;
        (formState as any).addToCalendar = isRecurring;
        
        await onSave(formState, fileInput);

        // If file was changed, trigger policy review
        if (fileChanged) {
          try {
            await reviewPolicy(companyID, formState.id);
            console.log('[PolicyEditDialog] Policy review triggered for:', formState.id);
          } catch (error) {
            console.error('[PolicyEditDialog] Failed to trigger policy review:', error);
          }
        }
      }
      onClose();
      toast.success(fileChanged ? 'Policy updated and review triggered' : 'Policy updated successfully');
    } catch (error) {
      console.error('Error saving policy:', error);
      setErrorMessage('Failed to save policy');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnUserAssign = (user: string) => {
    console.log('handleOnUserAssign', user);
    toast.promise(assignPolicy(companyID, formState.policyRef, user), {
      loading: 'Assigning user to policy...',
      success: 'User assigned.',
      error: {
        render: (err: Error) => `There was an issue assigning: ${err}`,
        duration: 10000,
      },
    })
  }
  const handleOnUserUnassign = (user: string) => {
    console.log('handleOnUserUnassign', user);

    toast.promise(unassignPolicy(companyID, formState.policyRef, user), {
      loading: 'Unassigning user from policy...',
      success: 'User unassigned.',
      error: {
        render: (err: Error) => `There was an issue unassigning: ${err}`,
        duration: 10000,
      },
    })
  }
  const handleAssignAll = () => {
    console.log('handleAssignAll')
    toast.promise(assignPolicyToAll(companyID, formState.policyRef), {
      loading: 'Assign policy to all users...',
      success: 'Assigned to all.',
      error: {
        render: (err: Error) => `There was an issue assigning: ${err}`,
        duration: 10000,
      },
    });
    // assignPolicyToAll(companyID, formState.policyRef)
    //   .then((res) => console.log(res));
  }

  const handleUnassignAll = () => {
    console.log('handleUnassignAll');
    toast.promise(unassignPolicyToAll(companyID, formState.policyRef), {
      loading: 'Unassign policy to all users...',
      success: 'Unassigned to all.',
      error: {
        render: (err: Error) => `There was an issue assigning: ${err}`,
        duration: 10000,
      },
    });
    // unassignPolicyToAll(companyID, formState.policyRef)
    //   .then((res) => console.log(res));
  }

  useEffect(() => {
    if (isOpen && policy) {
      console.log('[PolicyEditDialog', 'useEffect', policy);
      setFormState({
        ...policy,
        policy_name: policy.policy_name ?? '',
        policyCategory: policy.policyCategory ?? '',
        policy_version: policy.policy_version ?? '',
        policy_metadata: {
          ...policy?.policy_metadata,
          policyType: policy?.policy_metadata?.policyType ?? 'default',
          confluence: policy?.policy_metadata?.confluence ?? false
        }
      });
      setFormErrors({ policy_name: '', policyCategory: '', policy_version: '' });
    }
  }, [isOpen, policy]);

  // Compute control IDs from policy to seed MCM card
  const policyControlIds = useMemo(() => {
    const ids = new Set<string>();
    const rel = (formState as any)?.relatedControls;
    if (Array.isArray(rel)) rel.map(String).forEach((id) => id && ids.add(id));
    const procRef = (formState as any)?.procRef || (policy as any)?.procRef;
    if (Array.isArray(procRef)) procRef.map(String).forEach((id) => id && ids.add(id));
    const selected = (policy as any)?.selectedControls;
    if (Array.isArray(selected)) selected.map(String).forEach((id) => id && ids.add(id));
    return Array.from(ids);
  }, [formState.relatedControls, (formState as any)?.procRef, (policy as any)?.procRef, (policy as any)?.selectedControls]);

  // Default link for this entity (Policy)
  const defaultEntityLink = useMemo(() => {
    const ref = String((policy as any)?.policyRef || (formState as any)?.policyRef || (formState as any)?.id || (policy as any)?.id || '');
    return {
      id: `internal:policy:${ref}`,
      ref,
      title: String(formState.policy_name || policy?.policy_name || 'Policy'),
      domain: 'Policy',
      relation: 'implements',
    };
  }, [policy, formState.policy_name, (formState as any)?.policyRef, (formState as any)?.id]);

  // Ensure a chosen control is reflected locally on this policy
  const ensureControlLinked = useCallback(async (cid: string) => {
    setFormState((prev) => {
      const rel = Array.isArray(prev.relatedControls) ? prev.relatedControls.map(String) : [];
      const exists = rel.includes(cid);
      const next: any = { ...prev };
      if (!exists) next.relatedControls = [...rel, cid];
      // For document-backed policies, keep procRef in sync as well
      if (prev.policy_metadata?.policyType === 'document') {
        const pr = Array.isArray((prev as any).procRef) ? (prev as any).procRef.map(String) : [];
        if (!pr.includes(cid)) next.procRef = [...pr, cid];
      }
      return next;
    });
  }, []);

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileChange({ target: { files: [droppedFile] } } as any);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!policy) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="max-w-3xl! w-3xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                <FontAwesomeIcon icon={faShieldCheck} className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">{isReadOnly ? 'View Policy' : 'Edit Policy'}</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                {isReadOnly 
                  ? 'View policy details and linked controls.' 
                  : 'Update policy details or upload a new version.'}
              </p>
              
              {/* Download Button - shown for document library policies with files */}
              {hasDownloadableFile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="mt-4 gap-2"
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                  )}
                  {isDownloading ? 'Downloading...' : 'Download Policy Document'}
                </Button>
              )}
            </div>

            {/* Form Content - scrollable */}
            <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
              {/* Error Message */}
              {errorMessage && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
                </div>
              )}

              {/* Policy Name Card */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileLines} className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="policyName" className="block text-sm font-semibold text-foreground mb-1">
                      Policy Name {!isReadOnly && <span className="text-destructive">*</span>}
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      The display name for this policy
                    </p>
                    <Input
                      id="policyName"
                      placeholder="e.g., Information Security Policy"
                      value={formState.policy_name}
                      onChange={(e) => !isReadOnly && handleChange('policy_name', e.target.value)}
                      disabled={isReadOnly}
                      readOnly={formState.policy_metadata?.policyType !== 'upload' && !isReadOnly}
                      className={`bg-white/5 border text-base h-11 focus:border-primary/50 ${
                        formErrors.policy_name ? 'border-red-500' : 'border-primary/10'
                      }`}
                    />
                    {formErrors.policy_name && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.policy_name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Policy Source Card */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-cyan-500/10 to-cyan-500/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faCloudArrowUp} className="w-6 h-6 text-cyan-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Policy Source
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Where does this policy originate from?
                    </p>
                    <Select
                      value={formState.policy_metadata?.policyType || 'default'}
                      onValueChange={(value) => !isReadOnly && handlePolicySourceChange(value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11">
                        <SelectValue placeholder="Select policy source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">de.iterate Policy (default)</SelectItem>
                        <div className="px-2 pt-2 text-xs text-muted-foreground">Custom</div>
                        <SelectItem value="upload">Confluence / File Upload</SelectItem>
                        <SelectItem value="document">Document Library</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* File Upload Section - Only shown for upload type */}
              {formState.policy_metadata?.policyType === 'upload' && (
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    {formState.policy_metadata?.confluence ? 'Import from Confluence' : 'Upload Policy Document'}
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    {formState.policy_metadata?.confluence 
                      ? 'Enter the Confluence page URL to import'
                      : 'Upload a new version of this policy (will be saved to document library)'}
                  </p>

                  {/* Confluence Import */}
                  {formState.policy_metadata?.confluence ? (
                    <div className="space-y-3">
                      <Input
                        id="confluence-url"
                        placeholder="https://yourcompany.atlassian.net/wiki/spaces/..."
                        value={formState.policy_metadata?.confluenceURL || ''}
                        onChange={(e) => {
                          if (!isReadOnly) {
                            setFormState(prev => ({
                              ...prev,
                              policy_metadata: {
                                ...prev.policy_metadata,
                                confluenceURL: e.target.value,
                              },
                            }));
                          }
                        }}
                        className="bg-white/5 border-primary/10 h-11"
                        readOnly={isReadOnly}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          Copy from your browser's URL bar
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handlePullConfluence}
                          disabled={isImportingConfluence || isReadOnly || !formState.policy_metadata?.confluenceURL}
                        >
                          {isImportingConfluence ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            'Pull from Confluence'
                          )}
                        </Button>
                      </div>
                      {confluenceError && (
                        <p className="text-xs text-red-500">{confluenceError}</p>
                      )}
                    </div>
                  ) : (
                    /* File Upload */
                    <>
                      {fileInput ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <FileText className="w-8 h-8 text-emerald-500" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{fileInput.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(fileInput.size)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setFileInput(null)}
                            className="shrink-0"
                            disabled={isReadOnly}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          onDrop={handleDrop}
                          onDragOver={(e) => e.preventDefault()}
                          onClick={() => !isReadOnly && fileInputRef.current?.click()}
                          className={`border-2 border-dashed border-primary/20 rounded-lg p-8 text-center transition-all ${
                            isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/40 hover:bg-white/5'
                          }`}
                        >
                          <FontAwesomeIcon icon={faUpload} className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            PDF, DOC, or DOCX
                          </p>
                        </div>
                      )}
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                        disabled={isReadOnly}
                      />
                      
                      {formState.file_name && !fileInput && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Current file: <span className="text-foreground">{formState.file_name}</span>
                        </p>
                      )}
                    </>
                  )}

                  {/* Toggle between Confluence and File Upload */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Select
                      value={formState.policy_metadata?.confluence ? 'confluence' : 'upload'}
                      onValueChange={(value) => !isReadOnly && handleCustomSourceChange(value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-10 w-[200px]">
                        <SelectValue placeholder="Select source type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upload">File Upload</SelectItem>
                        <SelectItem value="confluence">Confluence Import</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Document File Info - Shown for document library policies */}
              {formState.policy_metadata?.policyType === 'document' && (formState.file_name || (formState as any).fileName || (formState as any).displayName) && (
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-blue-500/10 to-blue-500/30 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Policy Document
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        File stored in document library
                      </p>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <FileText className="w-8 h-8 text-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {(formState as any).displayName || formState.file_name || (formState as any).fileName || 'Unknown file'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Stored in Document Library
                          </p>
                        </div>
                        {hasDownloadableFile && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className="shrink-0 hover:bg-blue-500/20"
                            title="Download file"
                          >
                            {isDownloading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 text-blue-500" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Policy Owner Card */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-amber-500/10 to-amber-500/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faUserTie} className="w-6 h-6 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Policy Owner
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Person responsible for maintaining this policy
                    </p>
                    {isReadOnly ? (
                      <Input
                        value={formState.policy_history?.policy_owner ?
                          (userIdToName[formState.policy_history.policy_owner] || formState.policy_history.policy_owner) :
                          'No owner assigned'}
                        readOnly
                        className="bg-white/5 border-primary/10 h-11"
                      />
                    ) : (
                      <SingleUserCombobox
                        selected={formState.policy_history?.policy_owner || ''}
                        setSelected={(user) => setFormState(prev => ({ 
                          ...prev, 
                          policy_history: { ...prev.policy_history, policy_owner: user } 
                        }))}
                        className="bg-white/5 border-primary/10 h-11"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Review Schedule Card */}
              <div className="rounded-xl border border-primary/20 bg-linear-to-br from-primary/5 to-primary/15 backdrop-blur-sm p-6 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faCalendarClock} className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Review Schedule
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Set when this policy should be reviewed. Recurring policies will appear in your compliance calendar.
                    </p>
                  </div>
                </div>

                {/* Schedule Summary Badge */}
                {scheduleSummary && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 text-amber-500" />
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                      {scheduleSummary}
                    </p>
                  </div>
                )}

                {/* Last Review Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Last Reviewed
                    </Label>
                    <DatePicker
                      date={lastReviewDate ?? undefined}
                      setDate={(date) => !isReadOnly && setLastReviewDate(date ?? null)}
                      className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                    />
                  </div>
                </div>

                {/* Frequency Selection & Date Pickers */}
                {!isRecurring ? (
                  /* Non-recurring: Full width grid layout */
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Frequency Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Recurrence
                      </Label>
                      <Select
                        value={repeatFrequency}
                        onValueChange={(value) => {
                          if (isReadOnly) return;
                          const typed = value as RepeatFrequency;
                          setRepeatFrequency(typed);
                          if (typed === 'never') {
                            setStartDate(null);
                            setEndDate(null);
                          } else {
                            const base = cleanDateAndTimeObject(startDate ?? nextReviewDate ?? new Date()) ?? new Date();
                            setStartDate(base);
                          }
                        }}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full rounded-lg">
                          <SelectValue placeholder="Does not repeat" />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencyOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Next Review Date */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Next Review Date
                      </Label>
                      <DatePicker
                        date={nextReviewDate ?? undefined}
                        setDate={(date) => !isReadOnly && setNextReviewDate(date ?? null)}
                        className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                      />
                    </div>
                  </div>
                ) : (
                  /* Recurring: Flexible row layout */
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:flex-wrap">
                    {/* Frequency Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Recurrence
                      </Label>
                      <Select
                        value={repeatFrequency}
                        onValueChange={(value) => {
                          if (isReadOnly) return;
                          const typed = value as RepeatFrequency;
                          setRepeatFrequency(typed);
                          if (typed === 'never') {
                            setStartDate(null);
                            setEndDate(null);
                          } else {
                            const base = cleanDateAndTimeObject(startDate ?? nextReviewDate ?? new Date()) ?? new Date();
                            setStartDate(base);
                          }
                        }}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full sm:w-[180px] rounded-lg">
                          <SelectValue placeholder="Does not repeat" />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencyOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Start Date */}
                    <div className="space-y-2 flex-1 min-w-[150px]">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Start Date
                      </Label>
                      <DatePicker
                        date={startDate ?? undefined}
                        setDate={(date) => !isReadOnly && setStartDate(date ?? null)}
                        className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                      />
                    </div>

                    {/* End Date (Optional) */}
                    <div className="space-y-2 flex-1 min-w-[150px]">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        End Date (Optional)
                      </Label>
                      <DatePicker
                        date={endDate ?? undefined}
                        setDate={(date) => !isReadOnly && setEndDate(date ?? null)}
                        className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Assigned Users Card - Only show for non-document library policies */}
              {formState.policy_metadata?.policyType !== 'document' && (
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-violet-500/10 to-violet-500/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faUsers} className="w-6 h-6 text-violet-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Assigned Users
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Users who should read and acknowledge this policy
                      </p>
                      {isReadOnly ? (
                        <div className="flex flex-wrap gap-2">
                          {formState.assignedUsers && formState.assignedUsers.length > 0 ? (
                            formState.assignedUsers.map(userId => (
                              <div key={userId} className="px-3 py-1.5 bg-white/10 rounded-full text-xs text-foreground">
                                {userIdToName[userId] || userId}
                              </div>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No users assigned</span>
                          )}
                        </div>
                      ) : (
                        <MultiUserCombobox
                          selected={formState.assignedUsers}
                          setSelected={(users) => {
                            setFormState(prev => ({
                              ...prev,
                              assignedUsers: Array.isArray(users) ? users.filter(Boolean) : []
                            }));
                          }}
                          onUserSelect={handleOnUserAssign}
                          onUserDeSelect={handleOnUserUnassign}
                          onAllSelect={handleAssignAll}
                          onAllDeselect={handleUnassignAll}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Related Controls Accordion */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem value="controls" className="border-0">
                    <AccordionTrigger className="px-5 py-4 hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-linear-to-br from-emerald-500/10 to-emerald-500/30 flex items-center justify-center">
                          <FontAwesomeIcon icon={faLink} className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div className="text-left">
                          <span className="text-sm font-semibold text-foreground">Related Controls</span>
                          <span className="ml-2 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            {Array.isArray(formState.relatedControls) ? `${formState.relatedControls.length} mapped` : 'None'}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5">
                      <ScrollArea className="max-h-[300px] overflow-auto">
                        {isReadOnly ? (
                          <div className="flex flex-wrap gap-2">
                            {formState.relatedControls && formState.relatedControls.length > 0 ? (
                              formState.relatedControls.map(control => (
                                <div key={control} className="px-3 py-1.5 bg-white/10 rounded-full text-xs text-foreground">
                                  {control}
                                </div>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">No controls mapped</span>
                            )}
                          </div>
                        ) : (
                          <ControlCombobox
                            selected={formState.relatedControls}
                            setSelected={(controls) => {
                              setFormState(prev => ({
                                ...prev,
                                relatedControls: Array.isArray(controls) ? controls : []
                              }));
                            }}
                            className="bg-white/5 border-primary/10"
                          />
                        )}
                      </ScrollArea>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* MCM Mappings Card */}
              <MCMMappingsCard
                tenancyId={companyID}
                isReadOnly={isReadOnly}
                controlIds={policyControlIds}
                defaultEntityLink={defaultEntityLink}
                onEnsureControlLinked={ensureControlLinked}
                className="max-w-none mt-0!"
              />

              {/* Info Note */}
              {formState.policy_metadata?.policyType === 'upload' && fileInput && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4 flex items-start gap-3">
                  <FontAwesomeIcon icon={faSparkles} className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Note:</span> Uploading a new file will save this policy to the document library and trigger a review process for assigned users.
                  </p>
                </div>
              )}

              {/* Component ID - read only info */}
              <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Component ID</Label>
                <p className="text-sm text-foreground mt-1 font-mono">{(formState as any).componentID || formState.id}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
              <p className="text-xs text-muted-foreground">
                {isReadOnly 
                  ? 'Viewing policy in read-only mode' 
                  : formErrors.policy_name 
                    ? 'Please fix errors before saving' 
                    : 'Ready to save changes'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </Button>
                {!isReadOnly && (
                  <Button onClick={handleSubmit} disabled={isSubmitting} className="min-w-[120px]">
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog for File Change */}
      <Dialog open={showReviewWarning} onOpenChange={setShowReviewWarning}>
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="max-w-md! bg-transparent shadow-none border-0 p-0"
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl overflow-hidden shadow-2xl border border-white/10">
            <div className="p-6 border-b border-white/10 bg-amber-500/10">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Policy Review Required
                </DialogTitle>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-foreground mb-4">
                You have changed the policy file. This action will:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500">•</span>
                  Reset all reading progress for assigned users
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500">•</span>
                  Trigger a policy review process
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500">•</span>
                  Require users to re-read and acknowledge the updated policy
                </li>
              </ul>
              <p className="text-sm text-foreground mt-4 font-medium">
                Are you sure you want to continue?
              </p>
            </div>
            <div className="p-4 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReviewWarning(false);
                  setFileChanged(false);
                  setFileInput(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowReviewWarning(false);
                  handleSubmit();
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                Continue & Reset Progress
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PolicyEditDialog;
